import React, { createContext, useEffect, useState } from "react";
import usePosthog from "Hooks/usePosthog/usePosthog";
import posthog from "posthog-js";
import CONFIG from "config/config";

interface FeatureFlagsObj {
  [key: string]: boolean;
}

const isLocal = CONFIG.ENV === "local" || CONFIG.ENV === "development";

const defaultValues: { featureFlags: FeatureFlagsObj; loading: boolean } = {
  featureFlags: {},
  loading: true,
};

export const FeatureFlagContext = createContext(defaultValues);

export const FeatureFlagContextProvider = ({ children }: { children: React.ReactNode }) => {
  usePosthog();

  const flagsMap = {
    isAdminADMOrderDetailsEnabled: "is_admin_adm_order_details_enabled",
    isReviewDiscountEnabled: "is_review_discount_enabled",
  };

  const arrayOfFlags = Object.entries(flagsMap);

  // Hard overrides applied in every environment (including prod/PostHog).
  // Empty by default — add a flag here to force its value while testing,
  // before it's wired through PostHog.
  const fixedValueFlags: FeatureFlagsObj = {};

  const [featureFlags, setFeatureFlags] = useState<FeatureFlagsObj>({});
  const [loading, setLoading] = useState(true);

  const evaluateFeatureFlags = () => {
    try {
      if (isLocal) {
        const enabledFlags = arrayOfFlags.reduce((acc, [alias]) => {
          acc[alias] = true;
          return acc;
        }, {} as FeatureFlagsObj);
        setFeatureFlags({ ...enabledFlags, ...fixedValueFlags });
        setLoading(false);
        return;
      }

      const posthogAvailable = posthog && typeof posthog.isFeatureEnabled === "function" && CONFIG.POSTHOG_KEY;

      if (!posthogAvailable) {
        console.warn("PostHog is not initialized. Defaulting flags to false.");
        const disabledFlags = arrayOfFlags.reduce((acc, [alias]) => {
          acc[alias] = false;
          return acc;
        }, {} as FeatureFlagsObj);
        setFeatureFlags({ ...disabledFlags, ...fixedValueFlags });
        setLoading(false);
        return;
      }

      const currentFlags = arrayOfFlags.reduce((acc, [alias, realFlag]) => {
        acc[alias] = posthog.isFeatureEnabled(realFlag) || false;
        return acc;
      }, {} as FeatureFlagsObj);

      setFeatureFlags({ ...currentFlags, ...fixedValueFlags });
      setLoading(false);
    } catch (error) {
      console.error("Error evaluating feature flags:", error);
      const disabledFlags = arrayOfFlags.reduce((acc, [alias]) => {
        acc[alias] = isLocal ? true : false;
        return acc;
      }, {} as FeatureFlagsObj);
      setFeatureFlags({ ...disabledFlags, ...fixedValueFlags });
      setLoading(false);
    }
  };

  useEffect(() => {
    evaluateFeatureFlags();

    if (!isLocal && posthog) {
      posthog.onFeatureFlags(() => {
        evaluateFeatureFlags();
      });
    }
  }, []);

  return <FeatureFlagContext.Provider value={{ featureFlags, loading }}>{children}</FeatureFlagContext.Provider>;
};
